import { 
    API_ENDPOINTS,
    BottomAnchoredControls, 
    Button, 
    ButtonTypes, 
    Card, 
    CardBody, 
    customToast, 
    Icons, 
    PageLayout, 
    replaceKeyWithValue, 
    Roles, 
    route, 
    Sizes, 
    Stepper, 
    StringHelpers, 
    useAuthorisationContext, 
    useDisabledContext, 
    useForm, 
    useLangContext, 
    useNavigationGuard, 
    useStepper, 
    useUserContext,
    Variants, 
    WidthConstrainedContainer 
} from "carrier-fe";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { 
    DEFAULT_NAVIGATION_GUARD, 
    MIN_NUMBER_OF_UNITS, 
    ProductModule, 
    STEPS, 
    TOTAL_STEPS 
} from "../../../../util/Constants";
import { configureToshibaPayload } from "../../../../util/toshiba-utils";
import ToshibaStep1 from "../../../../components/toshiba-installation-journey/Step1";
import ToshibaStep2 from "../../../../components/toshiba-installation-journey/Step2";
import ToshibaStep3 from "../../../../components/toshiba-installation-journey/Step3";
import ToshibaStep4 from "../../../../components/toshiba-installation-journey/Step4";
import ToshibaStep5 from "../../../../components/toshiba-installation-journey/Step5";
import ToshibaStep6 from "../../../../components/toshiba-installation-journey/Step6";
import ToshibaStep7 from "../../../../components/toshiba-installation-journey/Step7";
import { ModuleNumbers } from "../../../../types/journey";

const defaultData = {
    // STEP 1
    installer_company_id: '',
    installer_user_id: '',

    // STEP 2
    product_parent_id: '',
    skip_step_5: false,

    // STEP 3
    product_child_id: '',

    // STEP 4
    commissioning_date: '',
    distributor_id: '',
    customer_id: '',

    //STEP 5
    products: Array.from({ length: 2 }, () => new ProductModule()),
    add_a2a: false,
    total_system_refrigerant: '',

    // STEP 6,
    additional_a2a: new ProductModule({ isA2A: true }),
};

export type ToshibaInstallationFormData = typeof defaultData;

function ToshibaJourney() {
    const navigate = useNavigate();
    const { installationId } = useParams();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const { crud } = useLangContext();
    const { disabled, setDisabled } = useDisabledContext();
    const { hasRoleAccess } = useAuthorisationContext();
    const { user } = useUserContext();
    const [ loading, setLoading ] = useState(false);
    const [ refetchStepQueries, setRefetchStepQueries ] = useState(false);
    const [ numberOfModules, setNumberOfModules ] = useState<ModuleNumbers>({
        indoor: 1,
        outdoor: 1,
    });
    const previousModuleDiffRef = useRef<ModuleNumbers>({
        indoor: 0,
        outdoor: 0,
    });

    const isEdit = installationId ? true : false;
    const skipStep1 = hasRoleAccess(Roles.ENGINEER) && !isEdit;

    const form = useForm(
        update, 
        defaultData, 
        { silentSuccess: true }
    );

    const { Submit, setValues, store } = form;
    const { skip_step_5, add_a2a } = store;

    const totalSteps = useMemo(
        () => skip_step_5 || !add_a2a
            ? TOTAL_STEPS - 1 
            : TOTAL_STEPS,
        [skip_step_5, add_a2a]
    );

    const { activeStep, nextStep, prevStep, isFirstStep, isLastStep } = useStepper({
        totalSteps,
        initialStep: skipStep1 ? STEPS.STEP_2 : STEPS.STEP_1,
    });

    useNavigationGuard({
        message: crud?.modals?.alert?.description?.at(0) || DEFAULT_NAVIGATION_GUARD,
        isEnabled: !isLastStep,
    });

    const title = useMemo(
        () =>
            isEdit
                ? replaceKeyWithValue(
                    crud?.pages?.edit.title || 'Edit :model',
                    'model',
                    crud?.models?.installation || 'Installation'
                )
                : replaceKeyWithValue(
                    crud?.pages?.create.title || 'Create :model',
                    'model',
                    crud?.models?.installation || 'Installation'
                ),
        [crud, isEdit]
    );

    const btnLabel = useMemo(() => {
        if (loading) {
            return { loadingLabel: '...', defaultLabel: '...' };
        }
        const stepType = isLastStep ? 'confirm' : 'next';

        const defaultLabel = crud?.buttons?.[stepType]?.default || (isLastStep ? 'Confirm' : 'Next');

        const loadingLabel =
            crud?.buttons?.[stepType]?.submitting || (isLastStep ? 'Confirming...' : 'Saving...');

        return { loadingLabel, defaultLabel };
    }, [loading, isLastStep, crud?.buttons]);

    async function update(data: Partial<ToshibaInstallationFormData>): Promise<any> {
        const payload = configureToshibaPayload(data, activeStep);
        const installationIdFromSearchParams = searchParams.get('installationId');
        const installationIdToUse = installationId || installationIdFromSearchParams;

        let response;
        // Create Installation - Initial step
        if (isFirstStep && !isEdit && !installationIdFromSearchParams) {
            response = await axios.post<{
                data: ToshibaInstallationFormData & { id: string }
                message: string;
            }>(route(
                API_ENDPOINTS.REGISTRATION.INSTALLATION.STORE), 
                payload
            );
            // Patch on every other step
        } else if (installationIdToUse && activeStep) {
            const modifiedStep = 
                activeStep === STEPS.STEP_5 && data.skip_step_5
                    ? STEPS.STEP_6 
                    : activeStep === STEPS.STEP_6 && !data.add_a2a 
                        ? STEPS.STEP_7
                        : activeStep;  

            response = await axios.patch<{
                data: ToshibaInstallationFormData & { id: string }
                message: string;
            }>(route(
                    API_ENDPOINTS.REGISTRATION.INSTALLATION.UPDATE, 
                    {
                        installationId: installationIdToUse || '',
                        step: String(modifiedStep),
                    }
                ),
                payload
            );
        } else {
            throw new Error('Unable to submit. Missing required data');
        }

        // Item created. happens at step 1 only
        if (response?.status === 201) {
            const { data } = response;
            nextStep({
                installationId: data?.data?.id,
            });
            // every other call
        } else if (response?.status === 200) {
            const { data } = response;
            if (isLastStep) {
                setDisabled(false);
                navigate(`/installation/${data?.data?.id}`);
                customToast({
                    title: data?.message,
                    variant: Variants.Success
                });
            } else {
                nextStep();
            }
        }
    };

    useEffect(() => {
        const installationIdFromSearchParams = searchParams.get('installationId');
        const installationIdToUse = installationId || installationIdFromSearchParams;

        if (
            !installationIdToUse 
            || activeStep === STEPS.STEP_7
        ) return;

        const loadStepData = async () => {
            setLoading(true);
            setDisabled(true);
    
            try {
                const { data: { data: storedStepData } } = 
                    await axios.get<{
                        data: Partial<ToshibaInstallationFormData>;
                        message: string;
                    }>(
                        route(API_ENDPOINTS.REGISTRATION.INSTALLATION.EDIT, {
                            installationId: installationIdToUse,
                            step: String(activeStep),
                       })
                    )
                ;

                const additionalA2A = storedStepData?.products?.find(
                    product => product.is_a2a
                ) || new ProductModule({ isA2A: true });

                if (
                    storedStepData?.products?.length !== undefined 
                    && storedStepData?.products?.length < MIN_NUMBER_OF_UNITS.GENERIC
                    && !storedStepData.skip_step_5
                ) {
                    storedStepData.products = Array.from({ length: 2 }, () => new ProductModule());
                } else if (
                    storedStepData.skip_step_5 
                    && storedStepData?.products?.length === MIN_NUMBER_OF_UNITS.A2A
                ) {
                    storedStepData.additional_a2a = additionalA2A;
                } else if (
                    storedStepData.add_a2a
                    && storedStepData.products?.length
                ) {
                    storedStepData.additional_a2a = additionalA2A;
                    
                    if (activeStep === STEPS.STEP_5) {
                        storedStepData.products = storedStepData.products.filter(
                            product => !product.is_a2a
                        );
                    }
                }

                setValues({
                    ...storedStepData,
                });
            } catch (error: any) {
                customToast({
                    title: error?.response?.data?.message,
                    variant: Variants.Danger,
                });
            } finally {
                setLoading(false);
                setDisabled(false);
            }
        };

        loadStepData();
    }, [activeStep, installationId, searchParams]);

    // Auto-submit Step 1 for engineers creating a new installation
    useEffect(() => {
        if (!skipStep1 || searchParams.get('installationId')) return;

        const autoSubmitStep1 = async () => {
            setLoading(true);
            setDisabled(true);

            try {
                const payload = configureToshibaPayload({
                    installer_company_id: user?.individual?.company_id,
                    installer_user_id: user?.id,
                }, STEPS.STEP_1);

                const response = await axios.post<{
                    data: ToshibaInstallationFormData & { id: string };
                    message: string;
                }>(
                    route(API_ENDPOINTS.REGISTRATION.INSTALLATION.STORE),
                    payload
                );

                if (response?.status === 201) {
                    setSearchParams((prev) => {
                        const currentParams = Object.fromEntries(prev.entries());
                        return {
                            ...currentParams,
                            installationId: response.data?.data?.id,
                            step: String(STEPS.STEP_2),
                        };
                    });
                    setValues({
                        installer_company_id: user?.individual?.company_id,
                        installer_user_id: user?.id,
                    });
                }
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message || 'An error occurred.',
                    variant: Variants.Danger,
                });
                navigate('/installation');
            } finally {
                setLoading(false);
                setDisabled(false);
            }
        };

        autoSubmitStep1();
    }, []);

    // Display values offset by 1 when Step 1 is skipped for engineers
    const displayActiveStep = useMemo(
        () => hasRoleAccess(Roles.ENGINEER) ? activeStep - 1 : activeStep,
        [activeStep]
    );

    const displayTotalSteps = useMemo(
        () => hasRoleAccess(Roles.ENGINEER) ? totalSteps - 1 : totalSteps,
        [totalSteps]
    );

    const renderBackButton = () => {
        if (isFirstStep) return false;
        const minStepForBackButton = 
            hasRoleAccess(Roles.ENGINEER) ? STEPS.STEP_3 : STEPS.STEP_2;
        return activeStep >= minStepForBackButton;
    };

    const stepComponents = useMemo(() => {
        const components = [
            ToshibaStep1, 
            ToshibaStep2, 
            ToshibaStep3, 
            ToshibaStep4,
            ToshibaStep5,
            ToshibaStep6,
            ToshibaStep7,
        ];

        if (skip_step_5) {
            components.splice(STEPS.STEP_4, STEPS.STEP_1);
        } else if (activeStep >= STEPS.STEP_5 && !add_a2a) {
            components.splice(STEPS.STEP_5, STEPS.STEP_1);
        }

        return components;
    }, [skip_step_5, add_a2a, activeStep]);

    return (
        <PageLayout
            title={StringHelpers.title(title)}
            progressBar={{ 
                activeStep: displayActiveStep, 
                totalSteps: displayTotalSteps,
            }}
        >
            <WidthConstrainedContainer>
            <Card style={{ maxWidth: '1440px' }}>
                <CardBody>
                    <div>
                        <Stepper
                            title={StringHelpers.upper(
                                `${crud?.sub_titles?.step || 'step'} ${displayActiveStep}/${displayTotalSteps}`
                            )}
                            activeStep={activeStep}
                            rightSlot={activeStep === STEPS.STEP_4 &&
                                <Button
                                    label={refetchStepQueries ?
                                        crud?.buttons?.refresh?.submitting :
                                        crud?.buttons?.refresh?.default
                                    }
                                    size={Sizes.Small}
                                    onClick={() => setRefetchStepQueries(true)}
                                    rightIcon={Icons.REFRESH}
                                    disabled={refetchStepQueries}
                                />
                            }
                        >
                        {stepComponents.map((StepComponent, index) => (
                                <StepComponent
                                    key={index}
                                    form={form}
                                    loading={loading}
                                    refetchStepQueries={refetchStepQueries}
                                    setRefetchStepQueries={setRefetchStepQueries}
                                    numberOfModules={numberOfModules}
                                    setNumberOfModules={setNumberOfModules}
                                    previousModuleDiffRef={previousModuleDiffRef}
                                />
                            )
                        )}
                        </Stepper>
                    </div>
                </CardBody>
            </Card>
            </WidthConstrainedContainer>
            <BottomAnchoredControls>
            <div className="me-auto ms-5">
                <Button
                    label={StringHelpers.title(crud?.buttons?.exit?.default || 'Exit')}
                    onClick={() => {
                        window.confirm(
                            crud?.modals?.alert?.description?.at(0) || 
                            DEFAULT_NAVIGATION_GUARD
                        ) && 
                        navigate('/dashboard');
                    }}
                    variant={Variants.Dark}
                    type={ButtonTypes.Outline}
                    disabled={disabled}
                />
            </div>
            {renderBackButton() && 
                <Button
                    label={crud?.buttons?.previous?.default || 'Previous'}
                    onClick={prevStep}
                    variant={Variants.Dark}
                    type={ButtonTypes.Outline}
                    className={'me-4'}
                    disabled={disabled}
                />
            }
            <Submit
                disabled={disabled || loading}
                defaultLabel={btnLabel.defaultLabel}
                loadingLabel={btnLabel.loadingLabel}
                useOnMouseDown
            />
            </BottomAnchoredControls>
        </PageLayout>
   );
};

export default ToshibaJourney;